const overviewIcon = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z"/></svg>';
const fullscreenIcon = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5"/></svg>';

class WohlFuhlGallery extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.setup(this.controller.signal).catch(() => {
      this.querySelector('.wm-gallery__open').hidden = true;
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.lightbox?.destroy();
    this.controller = null;
  }

  async setup(signal) {
    const { default: PhotoSwipeLightbox } = await import(this.dataset.lightboxModule);
    if (signal.aborted) return;
    const items = Array.from(this.querySelectorAll('.wm-gallery__item'));
    if (!items.length) return;
    const labels = this.dataset;
    let corePromise;
    let dimensionsPromise;
    let opening = false;
    const loadCore = () => {
      corePromise ??= import(labels.coreModule).catch((error) => {
        corePromise = null;
        throw error;
      });
      return corePromise;
    };
    const prepareDimensions = () => {
      dimensionsPromise ??= Promise.all(items.filter((item) => item.hasAttribute('data-wm-needs-size')).map(async (item) => {
        const image = item.querySelector('img');
        image.loading = 'eager';
        await image.decode().catch(() => {});
        item.dataset.pswpWidth = image.naturalWidth || image.width || 1024;
        item.dataset.pswpHeight = image.naturalHeight || image.height || 768;
        item.style.setProperty('--wm-ratio', item.dataset.pswpWidth / item.dataset.pswpHeight);
      }));
      return dimensionsPromise;
    };
    const lightbox = new PhotoSwipeLightbox({
      gallery: this,
      children: '.wm-gallery__item',
      pswpModule: () => corePromise,
      mainClass: 'wm-gallery-viewer',
      closeTitle: labels.close,
      zoomTitle: labels.zoom,
      arrowPrevTitle: labels.previous,
      arrowNextTitle: labels.next,
      errorMsg: labels.error,
      indexIndicatorSep: ' / ',
      bgOpacity: 1,
      preload: [1, 1],
      paddingFn: () => ({ top: 64, right: 12, bottom: 20, left: 12 })
    });
    this.lightbox = lightbox;

    const open = async (index) => {
      if (opening || lightbox.pswp) return;
      opening = true;
      try {
        await Promise.all([loadCore(), prepareDimensions()]);
        if (!signal.aborted) lightbox.loadAndOpen(index, { gallery: this });
      } catch {
        if (!signal.aborted) window.location.assign(items[index].href);
      } finally {
        opening = false;
      }
    };

    this.addEventListener('click', (event) => {
      const item = event.target.closest('.wm-gallery__item');
      if (!item || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      open(items.indexOf(item));
    }, { capture: true, signal });

    const openButton = this.querySelector('.wm-gallery__open');
    openButton.addEventListener('click', () => open(0), { signal });
    openButton.hidden = false;

    lightbox.on('beforeOpen', () => {
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    });
    lightbox.on('destroy', () => {
      document.body.style.overflow = this.previousOverflow;
    });
    lightbox.on('uiRegister', () => {
      const viewer = lightbox.pswp;
      viewer.element.setAttribute('aria-label', labels.viewer);
      viewer.ui.registerElement({
        name: 'overview',
        order: 6,
        isButton: true,
        title: labels.overview,
        ariaLabel: labels.overview,
        html: overviewIcon,
        onInit: (button) => {
          const text = document.createElement('span');
          text.textContent = labels.overview;
          button.append(text);
        },
        onClick: () => viewer.close()
      });

      if (typeof viewer.element.requestFullscreen !== 'function') return;
      viewer.ui.registerElement({
        name: 'fullscreen',
        order: 15,
        isButton: true,
        title: labels.fullscreen,
        ariaLabel: labels.fullscreen,
        html: fullscreenIcon,
        onInit: (button) => button.setAttribute('aria-pressed', 'false'),
        onClick: async (event, button) => {
          try {
            if (document.fullscreenElement === viewer.element) {
              await document.exitFullscreen();
            } else {
              await viewer.element.requestFullscreen();
            }
          } catch {
            button.setAttribute('aria-label', labels.fullscreenError);
            button.title = labels.fullscreenError;
          }
        }
      });
      const updateFullscreen = () => {
        const active = document.fullscreenElement === viewer.element;
        const button = viewer.element.querySelector('.pswp__button--fullscreen');
        button.setAttribute('aria-pressed', String(active));
        button.setAttribute('aria-label', active ? labels.exitFullscreen : labels.fullscreen);
        button.title = active ? labels.exitFullscreen : labels.fullscreen;
      };
      document.addEventListener('fullscreenchange', updateFullscreen);
      viewer.on('close', () => {
        if (document.fullscreenElement === viewer.element) document.exitFullscreen().catch(() => {});
      });
      viewer.on('destroy', () => document.removeEventListener('fullscreenchange', updateFullscreen));
    });
    lightbox.init();
  }
}

if (!customElements.get('wm-gallery')) customElements.define('wm-gallery', WohlFuhlGallery);
